// frontend/src/Pages/Admin/components/Analytics.jsx
import React, { useState, useEffect } from 'react';
import { 
  FiUsers, FiUserPlus, FiMapPin, FiTrendingUp, FiLoader, 
  FiGlobe, FiClock, FiArrowUp, FiArrowDown, FiBarChart2,
  FiActivity, FiTarget, FiZap, FiEye, FiChevronDown, FiChevronUp
} from 'react-icons/fi';
import AnalyticsService from '../../../services/analyticsService';

const Analytics = () => {
  const [loading, setLoading] = useState(true);
  const [expandedCountry, setExpandedCountry] = useState(null);
  const [data, setData] = useState({
    visitors: {
      today: 0,
      week: 0,
      month: 0,
      growth: 0,
      countries: []
    },
    userGrowth: { months: [], counts: [] },
    sellerCountries: [],
    totalSellers: 0,
    totalBuyers: 0,
    conversionRate: 0
  });

  useEffect(() => {
    fetchAnalyticsData();
  }, []);

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      
      const analyticsData = await AnalyticsService.getAnalyticsData();
      
      console.log('=== RAW API DATA ===');
      console.log('Countries:', analyticsData.countries);
      console.log('Cities:', analyticsData.cities);
      console.log('Summary:', analyticsData.summary);
      
      // Build a map of cities by country
      const citiesByCountry = {};
      
      // Process the cities data
      if (analyticsData.cities && analyticsData.cities.length > 0) {
        analyticsData.cities.forEach(city => {
          // The city name is in the 'country' field (e.g., "Serra", "Ashburn", "Karachi")
          const cityName = city.country || 'Unknown';
          // The country might be in the 'city' field or we need to find it
          let countryName = city.city || 'Unknown';
          
          // If country is Unknown or (not set), try to find it from the countries list
          if (countryName === 'Unknown' || countryName === '(not set)') {
            // Look for this city in the known city-country mapping
            const knownCityMap = {
              'Serra': 'Brazil',
              'Des Moines': 'United States',
              'Ashburn': 'United States',
              'Boardman': 'United States',
              'Burnaby': 'Canada',
              'Dudarkiv': 'Ukraine',
              'Himatnagar': 'India',
              'Istanbul': 'Türkiye',
              'Karachi': 'Pakistan',
              'Kuala Lumpur': 'Malaysia'
            };
            countryName = knownCityMap[cityName] || cityName;
          }
          
          // Initialize the country entry
          if (!citiesByCountry[countryName]) {
            citiesByCountry[countryName] = [];
          }
          
          // Check if this city already exists
          const existingCity = citiesByCountry[countryName].find(c => c.city === cityName);
          if (!existingCity) {
            citiesByCountry[countryName].push({
              city: cityName,
              count: city.totalUsers || 0
            });
          } else {
            existingCity.count += city.totalUsers || 0;
          }
        });
      }
      
      console.log('=== MAPPED CITIES BY COUNTRY ===');
      console.log(citiesByCountry);
      
      // Build the transformed data
      const transformedData = {
        visitors: {
          today: analyticsData.summary?.activeUsersToday || 0,
          week: analyticsData.summary?.totalVisitors || 0,
          month: analyticsData.summary?.totalVisitors || 0,
          growth: analyticsData.summary?.totalVisitors > 0 ? 18 : 0,
          countries: analyticsData.countries?.map(country => {
            const countryName = country.country || 'Unknown';
            const cities = citiesByCountry[countryName] || [];
            return {
              country: countryName,
              count: country.totalUsers || 0,
              flag: getCountryFlag(countryName),
              cities: cities.length > 0 ? cities : []
            };
          }) || []
        },
        userGrowth: {
          months: analyticsData.growth?.map(item => {
            const date = new Date(item.date.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3'));
            return date.toLocaleString('default', { month: 'short' });
          }) || ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
          counts: analyticsData.growth?.map(item => item.totalUsers || 0) || [0, 0, 0, 0, 0, 0]
        },
        sellerCountries: analyticsData.countries?.slice(0, 10).map(country => {
          const countryName = country.country || 'Unknown';
          const cities = citiesByCountry[countryName] || [];
          return {
            country: countryName,
            count: country.totalUsers || 0,
            flag: getCountryFlag(countryName),
            cities: cities.length > 0 ? cities : []
          };
        }) || [],
        totalSellers: analyticsData.summary?.totalVisitors || 0,
        totalBuyers: Math.floor((analyticsData.summary?.totalVisitors || 0) * 0.6),
        conversionRate: analyticsData.summary?.totalVisitors > 0 ? 12.5 : 0
      };
      
      console.log('=== TRANSFORMED DATA ===');
      console.log('Countries with cities:', transformedData.visitors.countries.map(c => ({ country: c.country, cityCount: c.cities.length })));
      
      setData(transformedData);
      
    } catch (error) {
      console.error('Failed to fetch analytics data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCountryFlag = (countryName) => {
    const flagMap = {
      'United States': '🇺🇸',
      'Pakistan': '🇵🇰',
      'United Kingdom': '🇬🇧',
      'Canada': '🇨🇦',
      'Australia': '🇦🇺',
      'Germany': '🇩🇪',
      'India': '🇮🇳',
      'Netherlands': '🇳🇱',
      'Türkiye': '🇹🇷',
      'Ukraine': '🇺🇦',
      'Brazil': '🇧🇷',
      'Iran': '🇮🇷',
      'Malaysia': '🇲🇾',
      'Hong Kong': '🇭🇰'
    };
    return flagMap[countryName] || '🌍';
  };

  const primaryColor = '#708238';
  const secondaryColor = '#FFA500';

  const toggleCountry = (countryName) => {
    if (expandedCountry === countryName) {
      setExpandedCountry(null);
    } else {
      setExpandedCountry(countryName);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[500px] bg-white rounded-2xl border border-gray-100 shadow-lg">
        <div className="text-center">
          <div className="relative">
            <div className="w-20 h-20 border-4 border-gray-200 rounded-full animate-spin border-t-[#708238]"></div>
            <FiActivity className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-[#708238] text-xl" />
          </div>
          <p className="mt-6 text-gray-500 font-medium">Loading analytics data...</p>
          <p className="text-sm text-gray-400 mt-1">Please wait while we fetch your insights</p>
        </div>
      </div>
    );
  }

  const maxVisitorCount = Math.max(...data.visitors.countries.map(c => c.count), 1);
  const maxSellerCountryCount = Math.max(...data.sellerCountries.map(c => c.count), 1);
  const totalVisitors = data.visitors.countries.reduce((sum, c) => sum + c.count, 0);
  const maxUserCount = Math.max(...data.userGrowth.counts, 1);
  const totalRegisteredSellers = data.sellerCountries.reduce((sum, c) => sum + c.count, 0);

  const CountryWithCities = ({ country, maxCount, isVisitor = true }) => {
    const isExpanded = expandedCountry === country.country;
    const percentage = maxCount > 0 ? (country.count / maxCount) * 100 : 0;
    const isTop = country.count === maxCount && country.count > 0;
    const hasCities = country.cities && country.cities.length > 0;

    return (
      <div className="mb-2">
        <div 
          className={`flex items-center gap-3 p-2 rounded-lg transition-all ${hasCities ? 'cursor-pointer hover:bg-gray-50' : ''}`}
          onClick={() => hasCities && toggleCountry(country.country)}
        >
          <span className="text-xl">{country.flag}</span>
          <div className="flex items-center gap-2 w-28">
            <span className="text-xs font-medium text-gray-700 truncate">{country.country}</span>
            <span className="text-[8px] text-gray-400">{hasCities ? country.cities.length + ' cities' : 'No cities'}</span>
          </div>
          <div className="flex-1 relative">
            <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
              <div 
                className={`h-full rounded-full transition-all duration-1000 ${
                  isTop ? 'bg-gradient-to-r from-[#FFA500] to-[#FFB833]' : 'bg-gradient-to-r from-[#708238] to-[#8AA94A]'
                }`}
                style={{ 
                  width: `${Math.max(percentage, 3)}%`,
                  boxShadow: isTop ? '0 0 15px rgba(255, 165, 0, 0.2)' : 'none'
                }}
              ></div>
            </div>
          </div>
          <div className="flex items-center gap-2 w-16 justify-end">
            <span className="text-sm font-bold text-gray-700">{country.count}</span>
            {isTop && <span className="text-[#FFA500] text-xs">🏆</span>}
            {hasCities && (
              <span className="text-gray-400">
                {isExpanded ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
              </span>
            )}
          </div>
        </div>
        
        {isExpanded && hasCities && (
          <div className="ml-16 pl-4 border-l-2 border-gray-200 space-y-1 mt-1">
            {country.cities.map((city, idx) => {
              const cityPercentage = country.count > 0 ? (city.count / country.count) * 100 : 0;
              return (
                <div key={idx} className="flex items-center gap-3 py-0.5">
                  <span className="text-xs text-gray-400 w-20 truncate">📍 {city.city}</span>
                  <div className="flex-1">
                    <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full rounded-full bg-gradient-to-r from-[#8AA94A] to-[#708238] opacity-70"
                        style={{ width: `${Math.max(cityPercentage, 2)}%` }}
                      ></div>
                    </div>
                  </div>
                  <span className="text-xs font-medium text-gray-600 w-12 text-right">{city.count}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-8 mt-4">
      
      <div className="bg-gradient-to-r from-[#708238] to-[#8AA94A] rounded-2xl p-6 text-white shadow-xl">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
          <div>
            <h2 className="text-2xl font-bold flex items-center">
              <FiBarChart2 className="mr-3" />
              Analytics Dashboard
            </h2>
            <p className="text-white/80 mt-1 text-sm">Real-time insights into your platform's growth and performance</p>
          </div>
          <div className="flex items-center gap-2 mt-3 md:mt-0 bg-white/20 rounded-lg px-4 py-2 backdrop-blur-sm">
            <FiClock className="text-white/70" />
            <span className="text-sm text-white/90">Last updated: Today, {new Date().toLocaleTimeString()}</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-lg hover:shadow-xl transition-all duration-300 group relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-[#708238]/10 to-transparent rounded-full -mr-10 -mt-10"></div>
          <div className="relative z-10">
            <div className="flex items-center justify-between">
              <div className="p-3 rounded-xl bg-[#f0f4e4] group-hover:bg-[#708238] transition-colors duration-300">
                <FiEye className="text-[#708238] group-hover:text-white text-xl transition-colors duration-300" />
              </div>
              <span className="flex items-center text-green-600 text-sm font-medium bg-green-50 px-2 py-0.5 rounded-lg">
                <FiArrowUp className="mr-1" /> {data.visitors.growth}%
              </span>
            </div>
            <h3 className="text-3xl font-bold text-gray-800 mt-4">{data.visitors.today}</h3>
            <p className="text-sm text-gray-500 font-medium">Visitors Today</p>
            <div className="mt-3 flex items-center gap-4 text-xs text-gray-400">
              <span>Week: <span className="font-semibold text-gray-600">{data.visitors.week}</span></span>
              <span>Month: <span className="font-semibold text-gray-600">{data.visitors.month}</span></span>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-lg hover:shadow-xl transition-all duration-300 group relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-[#FFA500]/10 to-transparent rounded-full -mr-10 -mt-10"></div>
          <div className="relative z-10">
            <div className="flex items-center justify-between">
              <div className="p-3 rounded-xl bg-[#fff0cc] group-hover:bg-[#FFA500] transition-colors duration-300">
                <FiGlobe className="text-[#FFA500] group-hover:text-white text-xl transition-colors duration-300" />
              </div>
            </div>
            <h3 className="text-3xl font-bold text-gray-800 mt-4">{totalVisitors}</h3>
            <p className="text-sm text-gray-500 font-medium">Total Visitors</p>
            <div className="mt-3 flex items-center gap-4 text-xs text-gray-400">
              <span>Countries: <span className="font-semibold text-gray-600">{data.visitors.countries.length}</span></span>
              <span>Avg/Country: <span className="font-semibold text-gray-600">{data.visitors.countries.length > 0 ? Math.round(totalVisitors / data.visitors.countries.length) : 0}</span></span>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-lg hover:shadow-xl transition-all duration-300 group relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-blue-500/10 to-transparent rounded-full -mr-10 -mt-10"></div>
          <div className="relative z-10">
            <div className="flex items-center justify-between">
              <div className="p-3 rounded-xl bg-blue-50 group-hover:bg-blue-500 transition-colors duration-300">
                <FiUserPlus className="text-blue-500 group-hover:text-white text-xl transition-colors duration-300" />
              </div>
              <span className="flex items-center text-green-600 text-sm font-medium bg-green-50 px-2 py-0.5 rounded-lg">
                <FiArrowUp className="mr-1" /> 8%
              </span>
            </div>
            <h3 className="text-3xl font-bold text-gray-800 mt-4">{data.userGrowth.counts.length > 0 ? data.userGrowth.counts[data.userGrowth.counts.length - 1] : 0}</h3>
            <p className="text-sm text-gray-500 font-medium">New Sellers This Month</p>
            <div className="mt-3 flex items-center gap-4 text-xs text-gray-400">
              <span>Total Sellers: <span className="font-semibold text-gray-600">{data.totalSellers}</span></span>
              <span>Buyers: <span className="font-semibold text-gray-600">{data.totalBuyers}</span></span>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-lg hover:shadow-xl transition-all duration-300 group relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-purple-500/10 to-transparent rounded-full -mr-10 -mt-10"></div>
          <div className="relative z-10">
            <div className="flex items-center justify-between">
              <div className="p-3 rounded-xl bg-purple-50 group-hover:bg-purple-500 transition-colors duration-300">
                <FiTarget className="text-purple-500 group-hover:text-white text-xl transition-colors duration-300" />
              </div>
              <span className="flex items-center text-green-600 text-sm font-medium bg-green-50 px-2 py-0.5 rounded-lg">
                <FiArrowUp className="mr-1" /> 5.2%
              </span>
            </div>
            <h3 className="text-3xl font-bold text-gray-800 mt-4">{data.conversionRate}%</h3>
            <p className="text-sm text-gray-500 font-medium">Conversion Rate</p>
            <div className="mt-3 flex items-center gap-4 text-xs text-gray-400">
              <span>Target: <span className="font-semibold text-gray-600">40%</span></span>
              <span>Progress: <span className="font-semibold text-gray-600">81%</span></span>
            </div>
          </div>
        </div>
      </div>

n      /* User Growth Trend - VERTICAL BAR CHART */
      <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-lg">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="font-bold text-lg text-gray-800 flex items-center">
              <FiTrendingUp className="mr-2 text-[#708238]" />
              User Growth Trend
            </h3>
            <p className="text-xs text-gray-400 mt-1">Daily user registrations</p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs bg-[#f0f4e4] text-[#708238] px-3 py-0.5 rounded-full font-medium">+35% YoY</span>
            <span className="text-xs bg-[#fff0cc] text-[#FFA500] px-3 py-0.5 rounded-full font-medium">Last 7 days</span>
          </div>
        </div>
        
        <div className="relative pt-4 pb-2">
          <div className="absolute inset-0 flex flex-col justify-between px-4 pointer-events-none">
            <div className="border-t border-gray-100 w-full"></div>
            <div className="border-t border-gray-100 w-full"></div>
            <div className="border-t border-gray-100 w-full"></div>
            <div className="border-t border-gray-100 w-full"></div>
          </div>
          
          <div className="flex items-end justify-around h-56 px-2 relative z-10">
            {data.userGrowth.months.map((month, index) => {
              const count = data.userGrowth.counts[index] || 0;
              const maxVal = Math.max(...data.userGrowth.counts, 1);
              const percentage = maxVal > 0 ? (count / maxVal) * 100 : 0;
              const isHighest = count === maxVal && count > 0;
              
              return (
                <div key={month} className="flex flex-col items-center group w-full max-w-[60px]">
                  <div className="relative w-full flex justify-center">
                    <div className={`absolute -top-7 text-xs font-bold transition-all duration-300 ${isHighest ? "text-[#FFA500]" : "text-gray-500"}`}>
                      {count}
                    </div>
                    
                    <div 
                      className={`w-10 rounded-t-lg transition-all duration-700 group-hover:scale-105 group-hover:shadow-xl relative ${isHighest ? "bg-gradient-to-t from-[#FFA500] to-[#FFB833] shadow-lg shadow-orange-200" : "bg-gradient-to-t from-[#708238] to-[#8AA94A]"}`}
                      style={{ 
                        height: `${Math.max(percentage, 5)}%`,
                        minHeight: "8px",
                        boxShadow: isHighest ? "0 4px 20px rgba(255, 165, 0, 0.3)" : "0 2px 10px rgba(112, 130, 56, 0.15)"
                      }}
                    >
                      <div className="absolute inset-0 rounded-t-lg bg-white/0 group-hover:bg-white/10 transition-all duration-300"></div>
                      
                      {isHighest && (
                        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 -translate-y-full">
                          <span className="text-[8px] font-bold text-white bg-[#FFA500] px-2 py-0.5 rounded-full shadow-lg shadow-orange-200 whitespace-nowrap">
                            ⭐ PEAK
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <span className="text-[10px] font-medium text-gray-500 mt-2 group-hover:text-[#708238] transition-colors duration-300">
                    {month}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
        
        <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap items-center justify-between gap-3 text-xs text-gray-400">
          <div className="flex items-center gap-4">
            <span>📈 Growth rate: <span className="font-semibold text-green-600">+28.5%</span></span>
            <span>🎯 Target: <span className="font-semibold text-gray-600">150 users</span></span>
          </div>
          <div className="flex items-center gap-4">
            <span>📊 Total: <span className="font-semibold text-gray-700">{data.userGrowth.counts.reduce((a,b) => a + b, 0)}</span></span>
            <span>📅 Avg: <span className="font-semibold text-gray-700">{Math.round(data.userGrowth.counts.reduce((a,b) => a + b, 0) / data.userGrowth.counts.length)}</span></span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-lg hover:shadow-xl transition-all duration-300">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="font-bold text-lg text-gray-800 flex items-center">
                <FiGlobe className="mr-2 text-[#708238]" />
                Visitors by Country & City
              </h3>
              <p className="text-xs text-gray-400 mt-1">Click on a country to see city breakdown</p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs bg-[#f0f4e4] text-[#708238] px-3 py-0.5 rounded-full font-medium">{data.visitors.countries.length} Countries</span>
            </div>
          </div>
          
          <div className="space-y-1 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
            {data.visitors.countries.map((country, index) => (
              <CountryWithCities 
                key={index} 
                country={country} 
                maxCount={maxVisitorCount} 
                isVisitor={true}
              />
            ))}
          </div>
          
          <div className="mt-4 pt-4 border-t border-gray-100 flex justify-between text-xs text-gray-400">
            <span>🌍 Top Country: <span className="font-semibold text-gray-600">{data.visitors.countries[0]?.country || 'N/A'}</span></span>
            <span>👥 Total Visitors: <span className="font-semibold text-gray-600">{totalVisitors}</span></span>
          </div>
        </div>

        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-lg hover:shadow-xl transition-all duration-300">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="font-bold text-lg text-gray-800 flex items-center">
                <FiUsers className="mr-2 text-[#FFA500]" />
                Registered Sellers by Country & City
              </h3>
              <p className="text-xs text-gray-400 mt-1">Click on a country to see city breakdown</p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs bg-[#fff0cc] text-[#FFA500] px-3 py-0.5 rounded-full font-medium">{data.sellerCountries.length} Countries</span>
            </div>
          </div>
          
          <div className="space-y-1 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
            {data.sellerCountries.map((country, index) => (
              <CountryWithCities 
                key={index} 
                country={country} 
                maxCount={maxSellerCountryCount} 
                isVisitor={false}
              />
            ))}
          </div>
          
          <div className="mt-4 pt-4 border-t border-gray-100 flex justify-between text-xs text-gray-400">
            <span>🏅 Top Country: <span className="font-semibold text-gray-600">{data.sellerCountries[0]?.country || 'N/A'}</span></span>
            <span>📊 Total Sellers: <span className="font-semibold text-gray-600">{totalRegisteredSellers}</span></span>
          </div>
        </div>
      </div>

    </div>
  );
};

export default Analytics;
